<template>
  <div class="view-container">
    <PageHeader v-if="!isFullscreen" title="لیست نوبت ها" :infoLearnUrl="infoLearnUrl" infoTitle="راهنمای ثبت قبض">
      <template #info>
        <p style="line-height:1.9; white-space:pre-line;">
          جهت ثبت قبض ابتدا دکمه قبض جدید را زده سپس نام بیمار را انتخاب کنید سپس خدمات و کالاهای مصرفی مورد نظر را از
          قسمت اقلام فاکتور برای بیمار مد نظر انتخاب کنید. در صورت نیاز به اضافه کردن ردیف جدید بر روی دکمه افزودن ردیف
          جدید کلیک کنید. در نهایت برای ثبت قبض روی دکمه «ثبت قبض» کلیک نمایید.

          پس از پرداخت هزینه توسط بیمار، بر روی دکمه «تسویه» کلیک کرده و هزینه پرداختی بیمار را ثبت کنید. برای تنظیمات
          چاپ قبض و چاپ قبض دلخواه به تنظیمات مطب مراجعه نمایید.

          جهت مشاهده فیلم و راهنمای کامل آموزشی بر روی لینک زیر کلیک نمایید.
        </p>
      </template>
    </PageHeader>
    <div class="patients-container">
      <div class="filters-container" v-if="!isFullscreen">
        <div class="sub-header">
          <h2 class="page-title">لیست نوبت‌های ثبت شده</h2>
          <BaseButton title="تمام صفحه" variant="primary" type="button" @click="openFullscreenWindow" />
        </div>
        <hr class="divider-line" />

        <div class="top-form-fields">
          <div class="top-row">
            <div class="show-visits-group">
              <label>نمایش نوبت های:</label>
              <div class="visit-checkboxes">
                <label class="checkbox-label">
                  <input type="checkbox" v-model="showNotVisited" />
                  <span>ویزیت نشده</span>
                </label>
                <label class="checkbox-label">
                  <input type="checkbox" v-model="showVisited" />
                  <span>ویزیت شده</span>
                </label>
              </div>
            </div>
            <!-- Show-visits sits here in the first column; date, doctor, and search are siblings and laid out responsively -->
          </div>
          <div class="form-group date-only-group">
            <label>فیلتر بر اساس تاریخ</label>
            <div class="date-with-button">
              <div class="p-date">
                <DatePicker v-model="selectedDate" format="jYYYY/jMM/jDD" display-format="jYYYY/jMM/jDD"
                  :editable="true" auto-submit />
              </div>
            </div>
          </div>
          <div class="form-group doctor-only">
            <label>پزشک</label>

            <GenericSelect key="doctor-select" v-model="fRdocid" entity-type="doctor" :store="doctorsComboStore"
              label-field="fDname" value-field="fDauto" secondary-field="fDnezamno"
              placeholder="جستجو بر اساس نام، تخصص، نظام پزشکی..." :enable-enter-search="true" :refetch-on-clear="true"
              :enable-infinite-scroll="false" @item-selected="onDoctorSelected" @search="onDoctorSearch"
              @clear="onDoctorClear">
              <template #option="{ fDname, fDtakhasos, fDnezamno, fDauto }">
                <div class="doctor-option">
                  <div class="doctor-main">
                    <strong>{{ fDname }}</strong>
                    <span class="specialty">تخصص: {{ fDtakhasos }}</span>
                  </div>
                  <div class="doctor-secondary">
                    <span v-if="fDnezamno" class="medical-code">نظام پزشکی: {{ fDnezamno }}</span>
                    <span class="doctor-id">ID: {{ fDauto }}</span>
                  </div>
                </div>
              </template>
            </GenericSelect>
          </div>
          <div class="form-group search-group">
            <label>جستجوی عمومی</label>
            <div class="search-with-button">
              <input id="general-search" type="text" v-model="searchQuery" @keyup.enter="applyGeneralSearch"
                placeholder="جستجوی بیمار (نام، کد ملی، پرونده، تلفن)" />
              <BaseButton title="جستجو" variant="primary" @click="applyGeneralSearch" />
            </div>
          </div>
          <!-- Mobile-only: Move the search button to below the filters (full-width) -->
          <div class="mobile-only mobile-search-btn-wrapper">
            <BaseButton class="mobile-search-btn" title="جستجو" variant="primary" @click="applyGeneralSearch" />
          </div>
        </div>
      </div>

      <div class="table-wrapper" :class="{ fullscreen: isFullscreen }">
        <Teleport to="body">
          <div v-if="floatingTooltip.visible" class="floating-tooltip"
            :style="{ left: floatingTooltip.left + 'px', top: floatingTooltip.top + 'px' }">{{ floatingTooltip.text }}
          </div>
        </Teleport>
        <BaseTable
          :items="filteredReceptions"
          :empty-colspan="14"
        >
          <template #empty>
            هیچ اطلاعاتی یافت نشد.
          </template>

          <template #head>
            <tr>
              <th>ردیف</th>
              <th>نوبت</th>
              <th>تاریخ نوبت</th>
              <th>ساعت حضور</th>
              <th>نام و نام خانوادگی بیمار</th>
              <th>نام و نام خانوادگی پزشک</th>
              <th>شماره پرونده</th>
              <th>کد ملی</th>
              <th>سن</th>
              <th>تلفن همراه</th>
              <th>تلفن ثابت</th>
              <th>نوع بیمه</th>
              <th>توضیحات</th>
              <th>عملیات</th>
            </tr>
          </template>

          <template #row="{ item: reception, index }">
            <td data-label="ردیف">{{ toPersianDigits(index + 1) }}</td>
            <td data-label="نوبت">{{ toPersianDigits(reception.fRnumber || reception.visitNumber) }}</td>
            <td data-label="تاریخ نوبت">{{ toPersianDigits(reception.fRdate || formatJalaliDate(reception.receptionDate)) }}</td>
            <td data-label="ساعت حضور">{{ toPersianDigits(reception.fRtime || reception.visitTime) }}</td>
            <td data-label="نام و نام خانوادگی بیمار">{{ reception.fNamefami || reception.patientName }}</td>
            <td data-label="نام و نام خانوادگی پزشک">{{ reception.fDname || reception.doctorName }}</td>
            <td data-label="شماره پرونده">{{ toPersianDigits(reception.fParno || reception.fileSerial) }}</td>
            <td data-label="کد ملی">{{ toPersianDigits(reception.pmeli || reception.nationalId) }}</td>
            <td data-label="سن">{{ toPersianDigits(reception.fAge || reception.age) }}</td>
            <td data-label="تلفن همراه">{{ toPersianDigits(reception.ptele || '') }}</td>
            <td data-label="تلفن ثابت">{{ toPersianDigits(reception.fTdesc2 || '') }}</td>
            <td data-label="نوع بیمه">{{ reception.fBdesc || '' }}</td>
            <td data-label="توضیحات">{{ reception.fRdescription || reception.notes || '' }}</td>
            <td class="actions-cell" data-label="عملیات">
              <!-- Actions are disabled in the list view; they can be enabled and hooked to methods if needed -->
              <button class="action-icon visited" aria-label="ویزیت شده" disabled
                @mouseenter.stop="showFloatingTooltip($event, 'ویزیت شده')" @mouseleave.stop="hideFloatingTooltip()"
                @focus.stop="showFloatingTooltip($event, 'ویزیت شده')" @blur.stop="hideFloatingTooltip()">
                <img src="/img/tick2.svg" alt="ویزیت شده" />
              </button>
              <button class="action-icon records" aria-label="سوابق" disabled
                @mouseenter.stop="showFloatingTooltip($event, 'سوابق')" @mouseleave.stop="hideFloatingTooltip()"
                @focus.stop="showFloatingTooltip($event, 'سوابق')" @blur.stop="hideFloatingTooltip()">
                <img src="/img/attachment1.svg" alt="سوابق" />
              </button>
            </td>
          </template>
        </BaseTable>
      </div>
    </div>
    <!-- Info is handled by PageHeader -->

    <div v-if="isModalOpen" class="modal-overlay" @click.self="isModalOpen = false">
      <div class="modal-content">
        <div class="selection-view">
          <div class="modal-sub-header">
            <div class="sub-header">
              <ModalHeader>
                <template #default>
                  <h2 class="page-title">انتخاب پزشک</h2>
                </template>
                <template #actions>
                  <button class="modal-close-btn">
                    <font-awesome-icon :icon="['fas', 'arrow-right']" />
                  </button>
                </template>
              </ModalHeader>
              <button class="select-btn" @click="confirmDoctorSelection" :disabled="!selectedDoctorInModal"
                id="responsive-style">
                انتخاب
              </button>
            </div>
            <hr class="divider-line" />
          </div>
          <input type="text" v-model="doctorSearchQuery" placeholder="جستجوی پزشک..." class="selection-search-input" />
          <div class="selection-table-wrapper">
            <table class="selection-table">
              <thead>
                <tr>
                  <th>نام و نام خانوادگی پزشک</th>
                  <th>تخصص</th>
                  <th>شماره نظام پزشکی</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="filteredDoctors.length === 0">
                  <td :colspan="3" class="empty-row">هیچ پزشکی پیدا نشد.</td>
                </tr>
                <tr v-for="doctor in filteredDoctors" :key="doctor.id" @click="selectedDoctorInModal = doctor"
                  :class="{ 'selected-row': selectedDoctorInModal === doctor }">
                  <td data-label="نام و نام خانوادگی پزشک">
                    {{ doctor.fullName }}
                  </td>
                  <td data-label="تخصص">{{ doctor.expert }}</td>
                  <td data-label="شماره نظام پزشکی">{{ doctor.mcrno }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import PageHeader from '@/components/PageHeader.vue';
import { ref, computed, onMounted, watch } from "vue";
import { useFloatingTooltip } from '@/composables/useFloatingTooltip'
import { useRoute } from "vue-router";
import { storeToRefs } from "pinia";
import { useReceptionsStore } from "@/stores/clinic/receptions.js";
import { useDoctorsStore } from "@/stores/clinic/doctors.js";
import DatePicker from "vue3-persian-datetime-picker";
import moment from "jalali-moment";
import ModalHeader from '@/components/ModalHeader.vue'
import BaseButton from '@/components/UiCompunents/BaseButton.vue'
import BaseTable from '@/components/UiCompunents/BaseTable.vue'
import { toPersianDigits, formatJalaliDate } from '@/utils/formatters.js';
import { useSettingStore } from '@/stores/clinic/setting';
import { useToast } from 'vue-toastification';
import GenericSelect from '@/components/generic-select.vue';
import { useDoctorsComboStore } from '@/stores/VueSelectStores/doctorsCombo';

// --- استور پذیرش‌ها ---
const receptionsStore = useReceptionsStore();
const { receptionsList, pagination, searchQuery, filters, searchColumn } = storeToRefs(receptionsStore);
const { fetchReceptions } = receptionsStore;

// --- استور دکترها ---
const doctorsStore = useDoctorsStore();
const doctorsComboStore = useDoctorsComboStore();

const { doctorsList } = storeToRefs(doctorsStore);
const { fetchDoctors } = doctorsStore;

const settingStore = useSettingStore();
const { fetchSetting } = settingStore;
// --- متغیرهای محلی ---
const route = useRoute();
const isFullscreen = ref(route.query.fullscreen === '1');
const selectedDate = ref(moment().format("jYYYY/jMM/jDD"));
const selectedDoctorFilter = ref(null);
const isModalOpen = ref(false);
const doctorSearchQuery = ref("");
const selectedDoctorInModal = ref(null);
const generalSearch = ref("");
const fRdocid = ref("");
const FRdone = ref("");
const appliedGeneralSearch = ref("");
// show visit status filters
const showNotVisited = ref(true);
const showVisited = ref(false);
// Info modal state & link
// Info modal link handled by PageHeader; no local state required
const infoLearnUrl = 'https://www.sinapardazesh.ir/matab-learning-web/';
const appointmentMode = ref(null)

const toast = useToast();

// --- تبدیل Jalali به Gregorian قبل از فرستادن به API ---
function getGregorianDate(jalaliDate) {
  return moment(jalaliDate, "jYYYY/jMM/jDD").format("YYYY-MM-DD");
}

const onDoctorSelected = (doctor) => {
  if (doctor) {
    fRdocid.value = doctor.fDauto;
  }
};
const onDoctorSearch = (query) => {
};
// --- بارگذاری دیتا هنگام mount ---
onMounted(async () => {
  await initializePage();
});

async function initializePage() {
  try {
    filters.value = [{ column: "F_rdate", operator: "=", value: selectedDate.value }]
    if (fRdocid.value) {
      filters.value = [
        ...filters.value,
        { column: "F_rdocid", operator: "=", value: fRdocid.value }
      ];
    }
    if (showNotVisited.value && showVisited.value) {
      filters.value = [
        ...filters.value,
        { column: "F_rdone", operator: "=", value: 0 }
      ];
    }
    if (showVisited.value && showNotVisited.value) {
      filters.value = [
        ...filters.value,
        { column: "F_rdone", operator: "=", value: 1 }
      ];
    }
    fetchReceptions(pagination.value.current_page);

    await Promise.all([
      doctorsComboStore.loadInitialData()
    ]);

    const cashSetting = await fetchSetting();
    if (!cashSetting) {
      toast.error("برای شما تنظیماتی وجود ندارد، لطفا تنظیمات را اعمال نمایید")
      return;
    }

    const printSettings = JSON.parse(cashSetting.printSettings);
    if (printSettings?.appointmentMode) {
      appointmentMode.value = printSettings.appointmentMode;
    }
  } catch (error) {
    console.error("Error initializing page:", error);
    toast.error("خطا در بارگذاری تنظیمات");
  }
}
const { floatingTooltip, showFloatingTooltip, hideFloatingTooltip } = useFloatingTooltip()

// --- اگر تاریخ تغییر کرد، دوباره fetch کن ---

// --- فیلتر لیست مراجعات ---
const filteredReceptions = computed(() => {
  let result = receptionsList.value || [];

  // فیلتر بر اساس تاریخ (مقایسه جلالی)
  if (selectedDate.value) {
    const selectedJalali = selectedDate.value;
    result = result.filter((r) => {
      const rJalali = moment(r.receptionDate, "YYYY-MM-DD").format(
        "jYYYY/jMM/jDD"
      );
      return rJalali === selectedJalali;
    });
  }

  // فیلتر بر اساس پزشک: اگر دکتر انتخاب شده باشد از آن فیلتر استفاده کن، در غیر این‌صورت اگر متن جستجو وجود داشت از آن استفاده شود
  if (selectedDoctorFilter.value) {
    const doctorFullName = selectedDoctorFilter.value.fullName;
    result = result.filter((r) => r.doctorName === doctorFullName);
  } else if (doctorSearchQuery.value && doctorSearchQuery.value.trim() !== "") {
    const dq = doctorSearchQuery.value.trim().toLowerCase();
    result = result.filter((r) => {
      const name = (r.fDname || r.doctorName || "").toString().toLowerCase();
      const expert = (r.doctorExpert || r.expert || "").toString().toLowerCase();
      return name.includes(dq) || expert.includes(dq);
    });
  }

  // فیلتر بر اساس جستجوی عمومی (نام بیمار، کد ملی، شماره پرونده، تلفن)
  if (appliedGeneralSearch.value && appliedGeneralSearch.value.trim() !== "") {
    const q = appliedGeneralSearch.value.trim().toLowerCase();
    result = result.filter((r) => {
      const name = (r.fNamefami || r.patientName || "").toString().toLowerCase();
      const national = (r.pmeli || r.nationalId || "").toString().toLowerCase();
      const fileNo = (r.fParno || r.fileSerial || "").toString().toLowerCase();
      const phone = (r.ptele || r.phone || r.mobile || "").toString().toLowerCase();
      const notes = (r.fRdescription || r.notes || "").toString().toLowerCase();

      return (
        name.includes(q) ||
        national.includes(q) ||
        fileNo.includes(q) ||
        phone.includes(q) ||
        notes.includes(q)
      );
    });
  }

  // Filter by visit status checkboxes
  if (!showNotVisited.value || !showVisited.value) {
    // If either is unchecked we need to filter; if both checked, keep all
    result = result.filter((r) => {
      const visited = isAppointmentVisited(r);
      return (showNotVisited.value && !visited) || (showVisited.value && visited);
    });
  }

  return result;
});


// --- فیلتر لیست دکترها در مودال ---
const filteredDoctors = computed(() => {
  if (!doctorSearchQuery.value) return doctorsList.value;

  const query = doctorSearchQuery.value.toLowerCase();
  return doctorsList.value.filter((d) =>
    d.fullName.toLowerCase().includes(query)
  );
});

// --- انتخاب پزشک از مودال ---
function confirmDoctorSelection() {
  if (selectedDoctorInModal.value) {
    selectedDoctorFilter.value = selectedDoctorInModal.value; // اعمال فیلتر
    isModalOpen.value = false; // بستن مودال
    selectedDoctorInModal.value = null; // ریست انتخاب
    // Reflect selected doctor's name in the search input for consistency
    doctorSearchQuery.value = selectedDoctorFilter.value.fullName || '';
  }
}

function applyGeneralSearch() {
  filters.value = [{ column: "F_rdate", operator: "=", value: selectedDate.value }]
  if (fRdocid.value) {
    filters.value = [
      ...filters.value,
      { column: "F_rdocid", operator: "=", value: fRdocid.value }
    ];
  }
  if (showNotVisited.value) {
    filters.value = [
      ...filters.value,
      { column: "F_rdone", operator: "=", value: 0 }
    ];
  }
  if (showVisited.value) {
    filters.value = [
      ...filters.value,
      { column: "F_rdone", operator: "=", value: 1 }
    ];
  }
  fetchReceptions(pagination.value.current_page);
}

function applyDoctorSearch() {
  // Keep this for Enter key behavior; treat typed query as active filter;
  // selectedDoctorFilter takes precedence anyway, so clear it when typing a new doctor
  if (doctorSearchQuery.value && doctorSearchQuery.value.trim() !== "") {
    selectedDoctorFilter.value = null;
  }
}

function clearDoctorFilter() {
  selectedDoctorFilter.value = null;
  doctorSearchQuery.value = "";
}

// determine visited state using multiple possible fields
function isAppointmentVisited(item) {
  if (!item) return false;
  // common fields: status, visited, isVisited, visitStatus, isVisitedBool, visitDate, visitedDate
  const status = (item.status || item.fRstatus || item.visitStatus || item.fRvisit || item.fRvisited || item.visited || item.isVisited || item.visitedDate || item.visitDate);
  if (typeof status === 'boolean') return status;
  if (typeof status === 'string') {
    const s = status.toLowerCase();
    return s === 'done' || s === 'visited' || s === 'true' || s === '1' || s === 'visited';
  }
  // if fields 'visitedDate' or 'visitDate' exist and non-empty -> visited
  if (item.visitedDate || item.visitDate) {
    return true;
  }
  return false;
}

function openFullscreenWindow() {
  const url = new URL(window.location.href);
  url.searchParams.set('fullscreen', '1');
  url.searchParams.set('date', getGregorianDate(selectedDate.value));
  window.open(url.toString(), '_blank', 'noopener,noreferrer');
}

// Info modal now handled by PageHeader; openInfoModal removed

// closeInfoModal handled by InfoModal via v-model


// --- تبدیل اعداد به فارسی ---
// date/number formatters are centralized in utils/formatters.js
</script>

<style scoped>
.view-container {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 20px;
}

.header {
  width: 100%;
  text-align: right;
  color: var(--text-color-primary);
  padding: 10px;

  border-radius: 20px;
}

.info-icon {
  width: 18px;
  height: 18px;
  object-fit: contain;
  vertical-align: middle;
  margin-right: 4px;
  margin-left: 4px;
  z-index: 2;
  cursor: pointer;
}

.modal-sub-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: sticky;
  top: 0;
  background-color: var(--background-color);
  gap: 10px;
  z-index: 99;
}

.sub-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  color: var(--text-color-secondary);
  width: 100% !important;

  .add {
    display: flex;
    align-items: center;
    padding: 10px 22px;
    border-radius: 10px;
    border: none;
    cursor: pointer;
    background-color: var(--background-color-button);
    color: var(--background-color);

    &:hover {
      background-color: var(--background-color-button-hover);
    }
  }
}

/* Date + button inline */
.date-with-button {
  display: flex;
  align-items: center;
  gap: 10px;
}

.patients-container {
  direction: rtl;
  padding: 25px;
  background-color: var(--background-color);
  border-radius: 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 40px;
  max-height: 100vh;
}

/* The main form container is now a flexbox column */
.patient-form-container {
  display: flex;
  flex-direction: column;
  gap: 20px;
  /* Gap between top and bottom sections */
}

/* --- New styles for the top section layout --- */
.top-section {
  display: flex;
  gap: 20px;
  align-items: stretch;
  /* Makes children have equal height */
}

.top-form-fields {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  /* align contents from right to left start */
  gap: 20px;
}

/* Utility class: show only on mobile */
.mobile-only {
  display: none;
}

/* --- End of new styles --- */

.top-section {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.date-only-group,
.doctor-only {

  align-items: stretch;
}

.top-row {
  display: flex;
  /* default to row */
  gap: 10px;
  align-items: center;
}

.date-only-group {
  min-width: 180px;
}

.doctor-only {
  min-width: 180px;
}

.date-only-group,
.doctor-only {
  margin: 0;
}

.search-group {
  flex: 1;
}

.show-visits-group {
  min-width: 180px;
}

.show-visits-group>label {
  color: var(--tex-color-thirdly);
  margin-bottom: 5px;
  display: block;
}

.top-row .show-visits-group {
  order: 0;
}

.top-row .date-only-group {
  order: 1;
}

.top-row .doctor-only {
  order: 2;
}

.visit-checkboxes {
  display: flex;
  gap: 10px;
  align-items: center;
  margin-top: 7px;
  margin-bottom: 7px;
}

.visit-checkboxes input[type="checkbox"] {
  width: 18px;
  height: 18px;
  accent-color: var(--background-color-button);
  cursor: pointer;
  transition: accent-color 0.2s ease;
}

.visit-checkboxes input[type="checkbox"]:hover {
  accent-color: var(--background-color-button-hover);
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: calc(0.2vw + 12px);
  color: var(--text-color-primary);
  cursor: pointer;
  transition: color 0.2s ease;
}

.checkbox-label:hover {
  color: var(--background-color-button);
}

.checkbox-label span {
  color: var(--text-color-primary);
  transition: color 0.2s ease;
}

.checkbox-label:hover span {
  color: var(--background-color-button);
}

.bottom-form-fields {
  display: grid;
  grid-template-columns: 3fr 1fr;
  gap: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group label {
  margin-bottom: 5px;
  color: var(--tex-color-thirdly);
  text-align: justify;
}

.required {
  color: #ff0f0f;
  margin-right: 4px;
}

.form-group input,
.form-group select,
.form-group textarea {
  padding: 5px;
  border: 1px solid var(--text-color-secondary);
  border-radius: 10px;
  text-align: start;
  width: 100%;
  background-color: var(--background-color-secondary);
  color: var(--text-color-primary);
}

.form-group input,
.form-group select {
  height: 34px;
}

input#doctor-name {
  border: none;
  border-radius: 0;
  height: 34px;
}

input#patient-name {
  border: none;
  border-radius: 0;
}

button.clear-btn {
  border-radius: 30%;
  padding: 5px;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  margin-left: 5px;
  background-color: #ff0f0f;
  color: #ffffff;
}

.custom-input {
  display: flex;
  align-items: center;
  border: 1px solid var(--text-color-secondary);
  border-radius: 10px;
  text-align: start;
  overflow: hidden;
  background-color: var(--background-color);
}

.icon {
  background-color: var(--text-color-secondary) !important;
  color: #ffffff;
  padding: 10px;
  height: 14px;
}

.form-group input:focus,
.form-group select:focus,
.form-group textarea:focus {
  outline: none;
  border-color: var(--text-color-primary);
}

.form-actions {
  display: flex;
  justify-content: flex-start;
}

.p-date {
  border: 1px solid var(--text-color-secondary);
  border-radius: 10px;
  text-align: start;
  overflow: hidden;
  background-color: var(--background-color);
  transition: border-color 0.2s ease, background-color 0.2s ease;
}

.p-date :deep(.vpd-container) {
  border: 1px solid rgba(var(--text-color-secondary-rgb), 0.12);
  border-radius: 0.75rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  background-color: var(--background-color);
}

.p-date :deep(.vpd-input-group label) {
  background-color: var(--background-color-button) !important;
}

.p-date :deep(.vpd-input-group input) {
  border: none !important;
  line-height: 32px !important;
  background-color: var(--background-color) !important;
  color: var(--text-color-primary) !important;
}

.p-date :deep(.vpd-input-group input::placeholder) {
  color: var(--text-color-secondary) !important;
  opacity: 1;
}

.p-date :deep(.vpd-input-group input:focus) {
  border: 1px solid transparent !important;
}

.p-date :deep(.vpd-icon-btn),
.p-date :deep(button.vpd-icon-btn),
.p-date :deep(.vpd-actions button) {
  background-color: var(--background-color-button) !important;
  color: var(--background-color) !important;
  transition: background-color 0.3s ease !important;
}

.p-date :deep(.vpd-icon-btn svg),
.p-date :deep(button.vpd-icon-btn svg),
.p-date :deep(.vpd-actions button svg),
.p-date :deep(.vpd-icon-btn svg path),
.p-date :deep(button.vpd-icon-btn svg path),
.p-date :deep(.vpd-actions button svg path) {
  fill: var(--background-color) !important;
  color: var(--background-color) !important;
  stroke: var(--background-color) !important;
}

.p-date :deep(.vpd-icon-btn:hover),
.p-date :deep(button.vpd-icon-btn:hover),
.p-date :deep(.vpd-actions button:hover) {
  background-color: var(--background-color-button-hover) !important;
}

/* `.submit-button` styles were removed — submit buttons now use `BaseButton` */

input#general-search {
  width: 100%;
  padding: 6px 8px;
  border: 1px solid var(--text-color-secondary);
  border-radius: 10px;
  text-align: start;
  background-color: var(--background-color);
  color: var(--text-color-primary);
  transition: border-color 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease;
}

input#general-search:focus {
  outline: none;
  border-color: var(--text-color-primary);
  box-shadow: 0 0 0 2px rgba(var(--background-color-button-rgb), 0.12);
}

input#general-search::placeholder {
  color: var(--text-color-secondary);
  opacity: 1;
}

/* Search button next to input */
.search-with-button {
  display: flex;
  align-items: center;
  gap: 8px;
}

.search-with-button input#general-search {
  flex: 1;
  /* allow the input to take the remaining space next to the button */
  width: auto;
  /* avoid forcing 100% width when inside the wrapper */
}

.search-btn {
  background-color: var(--background-color-button);
  color: var(--background-color);
  border: none;
  padding: 0 12px;
  height: 32px;
  /* match requested uniform height */
  border-radius: 10px;
  cursor: pointer;
}

.search-btn:hover {
  background-color: var(--background-color-button-hover);
}

/* Ensure doctor, date and general search inputs + buttons align height */
#doctor-search {
  height: 32px;
  padding: 4px 8px;
  box-sizing: border-box;
  border-radius: 8px;
  border: none;
  background-color: transparent;
  color: var(--text-color-primary);
}

.search-with-button input#general-search {
  height: 32px;
  padding: 4px 8px;
  box-sizing: border-box;
  border-radius: 8px;
  border: 1px solid var(--text-color-secondary) !important;
  background-color: var(--background-color) !important;
  color: var(--text-color-primary) !important;
}

#doctor-search {
  width: 100%;
}

#doctor-search:focus {
  outline: none;
}

#doctor-search::placeholder {
  color: var(--text-color-secondary);
  opacity: 1;
}

/* DatePicker inner input */
.date-only-group .p-date :deep(.vpd-input-group input) {
  height: 32px !important;
  line-height: 32px !important;
  padding: 0 8px !important;
}

/* Ensure the date container and search wrapper align vertically */
.date-with-button,
.search-with-button,
.custom-input {
  display: flex;
  align-items: center;
}

/* Adjust clear button to align with smaller height */
button.clear-btn {
  width: 28px;
  height: 28px;
  padding: 2px;
}

.actions-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 5px;
  margin: 0;
  background-color: transparent;
  border: none;
  cursor: pointer;
  font-size: calc(0.2vw + 14px);
  height: 100%;
  min-height: 32px;
  width: 100%;

  .visited {
    color: #1476f3;
  }

  .archive {
    color: #db9501;
  }
}

/* Inline tooltip CSS removed — floating tooltip used instead */

/* New action icon buttons with image and caption */
.action-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  /* icon above (vertical) */
  padding: 0 5px;
  margin: 0;
  background-color: transparent;
  border: none;
  cursor: pointer;
  font-size: calc(0.2vw + 14px);
  height: 100%;
  min-height: 36px;
  width: 100%;
  gap: 6px;
  color: var(--text-color-secondary);
  position: relative;
  /* allow tooltip absolute positioning */
}

.action-icon img {
  width: 18px;
  height: 18px;
  object-fit: contain;
}

/* Inline tooltip CSS removed — floating tooltip used instead */
.action-icon[disabled] {
  /* Keep disabled semantics but remove the dimmed visual; allow hover for tooltip */
  opacity: 1;
  /* Show normal pointer so tooltip behavior matches other tables' action buttons */
  cursor: pointer;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: calc(100dvh - 65px);
  margin-top: 65px;
  background-color: rgba(var(--text-color-secondary-rgb), 0.5);
  /* make this overlay cover everything including third sidebar */
  -webkit-backdrop-filter: saturate(180%) blur(5px) !important;
  backdrop-filter: saturate(180%) blur(5px) !important;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  /* Ensure overlay sits above third-sidebar (z-index:2000) */
}

.modal-content {
  background-color: var(--background-color);
  padding: 20px;
  border-radius: 30px;
  width: auto;
  max-height: 90vh;
  max-width: 90vw;
  overflow-y: auto;
  position: relative;
  direction: rtl;
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin: 10px;
}

/* Specific styling for the receipt info modal (do not affect other modals) */
.info-modal-content {
  width: auto;
  max-width: 720px;
  min-width: 320px;
  height: auto;
  max-height: 80vh;
  border-radius: 12px;
  padding: 18px 20px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
}

.info-modal-content .modal-sub-header {
  padding-top: 8px;
}

.info-modal-content .modal-body {
  padding: 6px 2px 12px 2px;
  overflow-y: auto;
}

.info-modal-content .modal-body p {
  font-size: 18px;
  line-height: 1.9;
  text-align: justify;
  direction: rtl;
  text-justify: inter-word;
}

@media (max-width: 480px) {
  .info-modal-content .modal-body p {
    font-size: 16px;
  }
}

/* Ensure the info modal becomes a bottom sheet on mobile */
@media (max-width: 768px) {
  .info-modal-overlay {
    align-items: flex-end !important;
    padding: 0 12px 12px !important;
  }

  .info-modal-content {
    width: 100% !important;
    max-width: 100% !important;
    margin: 0 !important;
    border-radius: 12px 12px 0 0 !important;
    padding: 12px !important;
    box-shadow: 0 -6px 30px rgba(0, 0, 0, 0.12) !important;
    transform: none !important;
  }
}

/* For smaller desktops and tablets, make the top filters a 2x2 grid: 
   show-visits + date on first row, doctor + search on second row */
@media (max-width: 1222px) {
  .top-form-fields {
    display: grid !important;
    grid-template-columns: 1fr 1fr !important;
    grid-auto-rows: auto;
    gap: 12px;
    align-items: start;
  }

  /* Use explicit named grid areas to ensure cells map correctly regardless of DOM order */
  .top-form-fields {
    grid-template-areas: 'show date' 'doctor search' !important;
  }

  .top-row {
    grid-area: show;
    display: flex;
    align-items: center;
  }

  .date-only-group {
    grid-area: date;
  }

  .doctor-only {
    grid-area: doctor;
  }

  .search-group {
    grid-area: search;
  }

  /* Ensure each grid child uses full width of its grid cell */
  .top-form-fields>* {
    width: 100%;
    box-sizing: border-box;
  }

  /* Place show-visits in top-left */
  .top-row {
    grid-column: 1 / 2;
    grid-row: 1 / 2;
  }

  /* Place date in top-right */
  .date-only-group {
    grid-column: 2 / 3;
    grid-row: 1 / 2;
  }

  /* Place doctor in bottom-left */
  .doctor-only {
    grid-column: 1 / 2;
    grid-row: 2 / 3;
  }

  /* Place search in bottom-right */
  .search-group {
    grid-column: 2 / 3;
    grid-row: 2 / 3;
  }

  .search-with-button {
    width: 100%;
  }
}

.modal-close-btn {
  border: none;
  font-size: calc(0.3vw + 12px);
  color: var(--text-color-secondary);
  background-color: transparent;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  cursor: pointer;
}

.selection-view {
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 300px;
}

input.selection-search-input {
  width: 100%;
  padding: 6px 8px;
  border: 1px solid var(--text-color-secondary);
  border-radius: 10px;
  text-align: start;
  background-color: var(--background-color);
  color: var(--text-color-primary);
  transition: border-color 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease;
}

input.selection-search-input:focus {
  outline: none;
  border-color: var(--text-color-primary);
  box-shadow: 0 0 0 2px rgba(var(--background-color-button-rgb), 0.12);
}

input.selection-search-input::placeholder {
  color: var(--text-color-secondary);
  opacity: 1;
}

.selection-table-wrapper {
  overflow: auto;
  max-height: 70vh;
  border: 1px solid var(--tex-color-thirdly) !important;
  border-radius: 10px;
}

.selection-table {
  width: 100%;
  text-align: right;
  font-size: calc(0.3vw + 8px);
  color: var(--text-color-primary);
  border-collapse: separate;
  border-spacing: 0;
}

.selection-table th,
.selection-table td {
  padding: 12px 15px;
  border-bottom: 1px solid rgba(var(--text-color-secondary-rgb), 0.12);
  white-space: nowrap;
}

.selection-table th {
  position: sticky;
  top: 0;
  z-index: 2;
  font-weight: bold;
  border-left: 1px solid rgba(var(--text-color-secondary-rgb), 0.12);
  background-color: var(--hover-background-color);
}

.selection-table td {
  border-left: 1px solid rgba(var(--text-color-secondary-rgb), 0.12);
}

.selection-table tbody tr {
  cursor: pointer;
}

.selection-table tbody tr.selected-row {
  background-color: var(--hover-background-color);
  /* A light blue color for highlighting */
  color: #1476f3;
  font-weight: bold;
}

.select-btn {
  border-radius: 10px;
  border: none;
  padding: 7px 20px;
  transition: background-color 0.2s ease, opacity 0.2s ease;

  /* --- Styles for when the button is ENABLED (a row is selected) --- */
  background-color: var(--background-color-button);
  /* Blue background */
  color: var(--text-color-forthly);
  cursor: pointer;
}

.select-btn:hover {
  /* --- Hover style for the ENABLED button --- */
  background-color: var(--background-color-button-hover);
  /* Darker blue on hover */
}

.select-btn:disabled {
  /* --- Styles for when the button is DISABLED (no row is selected) --- */
  background-color: var(--text-color-secondary, #9ca3af);
  /* Gray background */
  color: #ffffff;
  cursor: not-allowed;
  opacity: 0.6;
  /* Make it look faded */
}

@media (max-width: 768px) {
  .top-section {
    flex-direction: column;
    /* Stack image area on top of fields */
  }

  /* Keep the 2x2 grid behavior on narrow screens as well */
  .top-form-fields {
    display: grid !important;
    grid-template-columns: 1fr 1fr !important;
    gap: 10px;
    align-items: start !important;
  }

  .top-row {
    display: flex !important;
    gap: 10px;
    align-items: center;
    width: 100%;
  }

  /* On mobile, keep show-visits group in the top-left area; date/doctor/search are arranged by grid */
  .top-row .show-visits-group {
    order: 1;
  }

  .date-only-group {
    flex: 1;
  }

  /* Ensure visit checkboxes are side-by-side with date in the top row on mobile */
  .show-visits-group {
    width: auto;
  }

  .top-row .show-visits-group {
    width: auto !important;
  }

  /* remove legacy .date-doctor-group rules (date & doctor are now direct children) */
  .date-only-group,
  .doctor-only {
    width: 100% !important;
  }

  .show-visits-group {
    width: 100% !important;
  }

  .search-group {
    width: 100% !important;
  }

  /* Hide original search button beside input on mobile and show duplicate full-width button */
  .search-with-button .search-btn {
    display: none !important;
  }

  .mobile-search-btn-wrapper {
    display: block !important;
    grid-column: 1 / -1;
  }

  .mobile-search-btn {
    width: 100%;
    height: 32px;
  }

  /* don't override the 2x2 grid here (leave column layout to the 1222px rule) */
  .form-group.span-4 {
    grid-column: span 1;
  }

  .image-upload-area {
    min-height: 200px;
  }

  .table-wrapper {
    overflow: auto;
    /* Enables both horizontal and vertical scrolling */
    min-height: 100vh !important;
    border: none !important;
    border-radius: 0px;
  }

  /* Special styling for the actions cell */
  .actions-cell {
    justify-content: center;
    /* Center the buttons */
  }

  /* Inline tooltip styling removed - using floating tooltip instead */

  .action-btn {
    align-items: start !important;
  }

  .modal-overlay {
    align-items: end !important;
  }

  .modal-content {
    max-width: 95vw !important;
    width: 100% !important;
    max-height: 80vh !important;
    padding: 0 10px 10px 10px !important;
    border-radius: 20px 20px 0 0 !important;
    margin: 0 !important;
  }

  .modal-sub-header {
    padding-top: 10px !important;
  }

  .selection-table-wrapper {
    overflow: auto;
    /* Enables both horizontal and vertical scrolling */
    /*min-height: 100vh !important;*/
    border: none !important;
    border-radius: 0px;
  }

  .selection-table thead {
    /* 1. Hide the table header on mobile */
    display: none;
  }

  .selection-table tr {
    /* 2. Make each row a card */
    display: block;
    border: 1px solid var(--tex-color-thirdly) !important;
    border-radius: 8px;
    margin-bottom: 1rem;
    padding: 0.5rem;
  }

  .selection-table td {
    /* 3. Make each cell a full-width row inside the card */
    display: flex;
    justify-content: space-between;
    /* Puts label on right, value on left */
    align-items: center;
    padding: 0.75rem 0.5rem;
    border-bottom: 1px solid #f0f0f0;
    text-align: left;
    /* Align data to the left */
    white-space: normal;
    /* Allow text to wrap */
    border-left: none !important;
  }

  .selection-table td:last-child {
    border-bottom: none !important;
  }

  .selection-table td::before {
    content: attr(data-label);
    /* Use the text from the data-label */
    font-weight: bold;
    color: var(--tex-color-thirdly) !important;
    padding-left: 1rem;
    text-align: right;
  }
}
</style>
